import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import { GetIndicatorsRequest, SalesInsightsResponse, useSalesInsights } from '../../../../api/indicators';
import { DashboardFiltersContext } from '../filters/DashboardFiltersContext';
import { dateRange, formatDateTimeParam } from '../../../../helpers/dateUtils';
import { DashboardContext } from '../../DashboardContext';
import NumericChart from '../charts/numericChart/NumericChart';
import ProgressDoubleChart from '../charts/ProgressDoubleChart';


const SalesInsightAreaSales = () => {
    const { t } = useTranslation();
    const { filters, dateRangeConfig } = useContext(DashboardFiltersContext);
    const { companyConfig, refreshDate } = useContext(DashboardContext);

    const [params, setParams] = useState<GetIndicatorsRequest>({
        fields: 'totalSales,totalTax,netSales,transactionVolume,avgTransactionValue,activeStaff,activeDevices,totalDiscount,totalServiceCharge,totalBasketDiscount,totalCustomerDiscount,totalAmountPaidByPoints,totalItemDiscount,refunds',
        startDateTime: formatDateTimeParam(filters.periodFrom ? filters.periodFrom : dateRange("today", undefined, undefined, undefined, dateRangeConfig).startDate),
        endDateTime: formatDateTimeParam(filters.periodTo ? filters.periodTo : dateRange("today", undefined, undefined, undefined, dateRangeConfig).endDate),
        locations: filters?.locations?.join(',') ? filters?.locations?.join(',') : companyConfig?.locations?.map(location => location.id).join(','),
        locationArea: filters?.locationArea?.id
    });

    const {
        data: salesInsights,
        isLoading,
        isError,
        refetch: fetchSalesInsights
    } = useSalesInsights<SalesInsightsResponse>(params, 'sales-insights');

    useEffect(() => {
        if (refreshDate != null) {
            fetchSalesInsights();
        }
    }, [refreshDate]);

    let salesPercentage: number | undefined;
    if (salesInsights?.salesInsights?.totalTax && salesInsights?.salesInsights?.totalSalesIncTax
        && salesInsights?.salesInsights?.totalSalesIncTax !== 0
    ) {
        salesPercentage = (salesInsights.salesInsights.totalTax / salesInsights.salesInsights.totalSalesIncTax);
    }


    useEffect(() => {
        setParams({
            ...params,
            startDateTime: filters.periodFrom ? filters.periodFrom : dateRange("today", undefined, undefined, undefined, dateRangeConfig).startDate,
            endDateTime: filters.periodTo ? filters.periodTo : dateRange("today", undefined, undefined, undefined, dateRangeConfig).endDate,
            locations: filters?.locations?.join(',') ? filters?.locations?.join(',') : companyConfig?.locations?.map(location => location.id).join(','),
            locationArea: filters?.locationArea?.id
        });
    }, [filters]);

    useEffect(() => { fetchSalesInsights(); }, [params]);

    return (
        <>
            <Box sx={{ marginBottom: '4px' }}>
                <NumericChart
                    title={t("screens.dashboarding.charts.sales")}
                    subtitle={t("screens.dashboarding.charts.includingTax")}
                    alignment="double"
                    mainNumber={salesInsights?.salesInsights.totalSalesIncTax}
                    mainCurrency={salesInsights?.currency}
                    numbers={[
                        {
                            number: salesInsights?.salesInsights.totalTax,
                            description: t("screens.dashboarding.charts.totalTax"),
                            currency: salesInsights?.currency
                        },
                        {
                            number: salesPercentage,
                            description: t("screens.dashboarding.charts.overall"),
                            currency: salesInsights?.currency,
                            showAsPercentage: true
                        }
                    ]}
                    isError={isError}
                    isLoading={isLoading}
                />
            </Box>
            <Box sx={{ marginTop: '4px' }}>
                <NumericChart
                    title={t("screens.dashboarding.charts.netSales")}
                    subtitle={t("screens.dashboarding.charts.excludingTax")}
                    alignment="empty"
                    mainNumber={salesInsights?.salesInsights.netSalesExcTax}
                    mainCurrency={salesInsights?.currency}
                    isError={isError}
                    isLoading={isLoading}
                    linkChart={`${process.env.REACT_APP_LINK_BACK_OFFICE}Pages/Reporting/SalesBreakdown.aspx`}
                    buttonText={t("screens.dashboarding.charts.salesOverview")}
                />
            </Box>
        </>
    )
}

export default SalesInsightAreaSales;
